import { useState, useEffect } from 'react';
import { auth, EmailAuthProvider, reauthenticateWithCredential } from '../firebaseConfig';
import { updateEmail, updatePassword } from 'firebase/auth';
import endpoints, { api } from '../apiConfig';
import RoleManagement from '../components/RoleManagement';
import PaymentSettings, { useStripeOnboardingStatus } from '../components/PaymentSettings';
import toast from 'react-hot-toast';
import { useDropzone } from 'react-dropzone';
import { useUserRole } from '../hooks/useUserRole';
import styles from './Account.module.css';

const OnboardingChecklist = ({ userData }) => {
  const { userRole, loading: roleLoading } = useUserRole();
  const { status, loading, error, refetch } = useStripeOnboardingStatus();
  const [artworkStats, setArtworkStats] = useState({ total: 0, published: 0 });
  const [backendProgress, setBackendProgress] = useState(null);
  
  // Fetch artwork statistics and backend progress
  useEffect(() => {
    const fetchData = async () => {
      if (!userData || userRole !== 'artist') return;
      
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      if (!token) return;
      
      try {
        // Fetch artwork stats
        const artworkResponse = await api.get(endpoints.artwork.myArtworks);
        
        const artworks = artworkResponse.data || [];
        const total = artworks.length || 0;
        const published = artworks.filter(artwork => 
          artwork.status === 'published' || artwork.shopify_product_id
        ).length || 0;
        
        setArtworkStats({ total, published });
        
        // Also fetch backend progress as fallback
        try {
          const progressResponse = await api.get(endpoints.artistProgress.status(userData.email));
          setBackendProgress(progressResponse.data.progress);
        } catch (progressError) {
          console.warn('Failed to fetch backend progress:', progressError);
        }
        
      } catch (error) {
        console.warn('Failed to fetch data:', error);
      }
    };
    
    fetchData();
  }, [userData, userRole]);

  // Also refresh data when component mounts or becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && userData && userRole === 'artist') {
        // Refetch when page becomes visible
        const fetchData = async () => {
          try {
            // Fetch artwork stats
            const artworkResponse = await api.get(endpoints.artwork.myArtworks);
            const artworks = artworkResponse.data || [];
            const total = artworks.length || 0;
            const published = artworks.filter(artwork => 
              artwork.status === 'published' || artwork.shopify_product_id
            ).length || 0;
            
            setArtworkStats({ total, published });
            
            // Also fetch backend progress
            try {
              const progressResponse = await api.get(endpoints.artistProgress.status(userData.email));
              setBackendProgress(progressResponse.data.progress);
            } catch (progressError) {
              console.warn('Failed to fetch backend progress:', progressError);
            }
            
          } catch (error) {
            console.warn('Failed to fetch data:', error);
          }
        };
        fetchData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [userData, userRole]);
  
  // Don't show for collectors - they have different needs
  if (roleLoading || userRole === 'collector') {
    return null;
  }
  
  // Check actual user data for completion status
  const hasProfile = userData && userData.bio && userData.bio.trim().length > 0;
  // For artwork, check actual count OR backend progress as fallback
  const hasArtwork = artworkStats.total > 0 || (backendProgress && backendProgress.artwork_uploaded);
  const hasStripeConnected = status && status.status === 'active';
  // For publishing, require BOTH Stripe connection AND actual published artworks
  const hasPublishedArtwork = hasStripeConnected && artworkStats.published > 0;
  
  // Seller onboarding steps (artist, gallery, curator)
  const steps = [
    { label: 'Complete Profile', complete: hasProfile },
    { label: 'Upload Artwork', complete: hasArtwork },
    { label: 'Connect Stripe Account', complete: hasStripeConnected },
    { label: 'Publish Artwork', complete: hasPublishedArtwork },
  ];
  const needsStripe = !loading && (!status || status.status !== 'active');

  return (
    <div style={{
      background: 'var(--color-white)',
      border: '1px solid var(--border-color)',
      borderRadius: 'var(--radius-lg)',
      boxShadow: 'var(--shadow-md)',
      padding: 'var(--spacing-lg)',
      marginBottom: 'var(--spacing-lg)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
    }}>
      <h3 style={{
        color: 'var(--text-primary)',
        fontSize: 'var(--font-size-lg)',
        fontWeight: 600,
        marginBottom: 'var(--spacing-md)'
      }}>Get Ready to Sell Your Artwork</h3>
      <ul style={{
        listStyle: 'none',
        padding: 0,
        margin: 0,
        marginBottom: 'var(--spacing-md)',
        width: '100%',
        maxWidth: 400,
      }}>
        {steps.map((step, idx) => (
          <li key={step.label} style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: idx === steps.length - 1 ? 0 : 'var(--spacing-sm)',
            color: step.complete ? 'var(--color-success)' : 'var(--text-secondary)',
            fontWeight: step.complete ? 600 : 400,
            fontSize: 'var(--font-size-base)'
          }}>
            <span style={{
              display: 'inline-block',
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: step.complete ? 'var(--color-success)' : 'var(--color-gray-300)',
              color: 'var(--color-white)',
              marginRight: 12,
              lineHeight: '20px',
              textAlign: 'center',
              fontWeight: 700
            }}>{step.complete ? '✓' : ''}</span>
            {step.label}
          </li>
        ))}
      </ul>
      {needsStripe && (
        <div style={{
          marginTop: 'var(--spacing-md)',
          color: 'var(--color-danger)',
          fontWeight: 500
        }}>
          You must finish your Stripe onboarding to publish and sell artwork.<br />
          <button
            type="button"
            className={styles.primaryButton}
            onClick={async () => {
              try {
                const response = await api.post('/stripe/connect/account-link');
                window.location.href = response.data.url;
              } catch (err) {
                toast.error('Error launching Stripe onboarding: ' + err.message);
              }
            }}
          >
            Finish Stripe Setup
          </button>
        </div>
      )}
    </div>
  );
};

const CollectorOnboardingChecklist = () => {
  const { userRole, loading: roleLoading } = useUserRole();
  
  // Only show for collectors
  if (roleLoading || userRole !== 'collector') {
    return null;
  }
  
  // Collector onboarding steps
  const steps = [
    { label: 'Complete Profile', complete: true },
    { label: 'Set Art Preferences', complete: false },
    { label: 'Follow Your First Artist', complete: false },
    { label: 'Save Artworks to Wishlist', complete: false },
  ];

  return (
    <div style={{
      background: 'var(--color-white)',
      border: '1px solid var(--border-color)',
      borderRadius: 'var(--radius-lg)',
      boxShadow: 'var(--shadow-md)',
      padding: 'var(--spacing-lg)',
      marginBottom: 'var(--spacing-lg)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
    }}>
      <h3 style={{
        color: 'var(--text-primary)',
        fontSize: 'var(--font-size-lg)',
        fontWeight: 600,
        marginBottom: 'var(--spacing-md)'
      }}>Welcome to Art Emerge</h3>
      <p style={{
        color: 'var(--text-secondary)',
        marginBottom: 'var(--spacing-md)',
        maxWidth: 400
      }}>
        Discover and collect amazing artworks from emerging artists around the world.
      </p>
      <ul style={{
        listStyle: 'none',
        padding: 0,
        margin: 0,
        marginBottom: 'var(--spacing-md)',
        width: '100%',
        maxWidth: 400,
      }}>
        {steps.map((step, idx) => (
          <li key={step.label} style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: idx === steps.length - 1 ? 0 : 'var(--spacing-sm)',
            color: step.complete ? 'var(--color-success)' : 'var(--text-secondary)',
            fontWeight: step.complete ? 600 : 400,
            fontSize: 'var(--font-size-base)'
          }}>
            <span style={{
              display: 'inline-block',
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: step.complete ? 'var(--color-success)' : 'var(--color-gray-300)',
              color: 'var(--color-white)',
              marginRight: 12,
              lineHeight: '20px',
              textAlign: 'center',
              fontWeight: 700
            }}>{step.complete ? '✓' : ''}</span>
            {step.label}
          </li>
        ))}
      </ul>
      <div style={{
        display: 'flex',
        gap: 'var(--spacing-sm)',
        flexWrap: 'wrap',
        justifyContent: 'center'
      }}>
        <button 
          className={styles.primaryButton}
          onClick={() => window.location.href = '/explore'}
        >
          Explore Artworks
        </button>
        <button 
          className={styles.secondaryButton}
          onClick={() => window.location.href = '/browse'}
        >
          Browse Artists
        </button>
      </div>
    </div>
  );
};

const Account = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    bio: '',
    artist_resume: '',
    artist_statement: ''
  });
  const [emailForm, setEmailForm] = useState({
    currentPassword: '',
    newEmail: ''
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [preferences, setPreferences] = useState({
    newsletters: false,
    artworkUpdates: false,
    exhibitionNotices: false,
    marketingEmails: false
  });
  const [roleUpdateLoading, setRoleUpdateLoading] = useState(false);
  const accountModalOpen = showEditModal || showEmailModal || showPasswordModal || showPreferencesModal;

  useEffect(() => {
    if (!accountModalOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [accountModalOpen]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png']
    },
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024, // 5MB
    onDrop: async (acceptedFiles) => {
      if (acceptedFiles?.length > 0) {
        await handleAvatarUpload(acceptedFiles[0]);
      }
    },
    onDropRejected: (fileRejections) => {
      const error = fileRejections[0]?.errors[0];
      if (error?.code === 'file-too-large') {
        toast.error('Image must be smaller than 5MB');
      } else if (error?.code === 'file-invalid-type') {
        toast.error('Only JPEG, JPG, and PNG files are allowed');
      } else {
        toast.error('Invalid file. Please try again.');
      }
    }
  });

  useEffect(() => {
    fetchUserData();
  }, []);

  useEffect(() => {
    if (userData) {
      setEditForm({
        name: userData.name || '',
        bio: userData.bio || '',
        artist_resume: userData.artist_resume || '',
        artist_statement: userData.artist_statement || ''
      });
    }
  }, [userData]);

  useEffect(() => {
    if (userData?.email_preferences) {
      console.log('Loading email preferences:', userData.email_preferences);
      setPreferences({
        newsletters: userData.email_preferences.newsletters || false,
        artworkUpdates: userData.email_preferences.artworkUpdates || false,
        exhibitionNotices: userData.email_preferences.exhibitionNotices || false,
        marketingEmails: userData.email_preferences.marketingEmails || false
      });
    }
  }, [userData]);

  const fetchUserData = async () => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const response = await api.get(endpoints.user.profile, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserData(response.data);
    } catch (err) {
      setError('Failed to fetch user data');
      console.error('Error fetching user data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = await auth.currentUser?.getIdToken();
      const response = await api.put(
        endpoints.user.update,
        { name: editForm.name },
        { headers: { Authorization: `Bearer ${token}` }}
      );

      if (response.data) {
        await fetchUserData();
        setShowEditModal(false);
        toast.success('Name updated successfully');
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      toast.error(err.response?.data?.message || 'Failed to update profile');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRoleUpdate = async (newRole) => {
    setRoleUpdateLoading(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      await api.put(
        endpoints.user.updateRole,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      await fetchUserData();
    } catch (err) {
      setError('Failed to update role');
      console.error('Error updating role:', err);
    } finally {
      setRoleUpdateLoading(false);
    }
  };

  const handleEmailChange = async (e) => {
    e.preventDefault();
    try {
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(
        user.email,
        emailForm.currentPassword
      );
      
      // Reauthenticate
      await reauthenticateWithCredential(user, credential);
      
      // Update email in Firebase
      await updateEmail(user, emailForm.newEmail);
      
      // Update email in database
      const token = await user.getIdToken();
      await api.put(
        endpoints.user.update,
        { email: emailForm.newEmail },
        { headers: { Authorization: `Bearer ${token}` }}
      );

      await fetchUserData();
      setShowEmailModal(false);
      toast.success('Email updated successfully');
    } catch (err) {
      console.error('Error updating email:', err);
      toast.error(err.message || 'Failed to update email');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    try {
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(
        user.email,
        passwordForm.currentPassword
      );
      
      // Reauthenticate
      await reauthenticateWithCredential(user, credential);
      
      // Update password
      await updatePassword(user, passwordForm.newPassword);
      
      setShowPasswordModal(false);
      toast.success('Password updated successfully');
    } catch (err) {
      console.error('Error updating password:', err);
      toast.error(err.message || 'Failed to update password');
    }
  };

  const handleAvatarUpload = async (file) => {
    let previewUrl = null;
    try {
      setIsUploading(true);
      setUploadProgress(0);
      
      // Create a preview URL for immediate feedback
      previewUrl = URL.createObjectURL(file);
      console.log('Created preview URL:', previewUrl);
      setUserData(prevData => ({
        ...prevData,
        avatar: previewUrl
      }));
      
      console.log('Starting avatar upload...', file);
      const formData = new FormData();
      formData.append('avatar', file);

      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        toast.error('You must be logged in to upload a profile picture');
        setIsUploading(false);
        return;
      }

      console.log('Sending upload request...');
      const response = await api.post(
        endpoints.user.uploadAvatar,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
            console.log('Upload progress:', progress + '%');
          }
        }
      );

      console.log('Upload response:', response.data);
      if (response.data && response.data.user && response.data.user.avatar) {
        console.log('Received avatar URL:', response.data.user.avatar);
        
        // Create a new Image object to preload the Cloudinary URL
        const img = new Image();
        
        img.onload = () => {
          console.log('New avatar image loaded successfully');
          setUserData(prevData => ({
            ...prevData,
            avatar: response.data.user.avatar
          }));
          
          // Clean up the preview URL
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
          }
          
          toast.success('Profile picture updated successfully');
        };

        img.onerror = () => {
          console.error('Failed to load new avatar image');
          // Revert to previous avatar
          fetchUserData();
          toast.error('Failed to load the uploaded image');
        };

        // Start loading the image
        img.src = response.data.user.avatar;
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      console.error('Error uploading avatar:', err);
      console.error('Error details:', err.response?.data);
      
      // Clean up the preview URL
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      
      // Revert to previous avatar if upload failed
      await fetchUserData();
      
      if (err.response?.status === 413) {
        toast.error('Image file is too large. Maximum size is 5MB');
      } else if (err.response?.status === 415) {
        toast.error('Invalid file type. Please use JPEG, JPG, or PNG');
      } else {
        toast.error(err.response?.data?.error || 'Failed to upload profile picture. Please try again.');
      }
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Modify the avatar display code
  const renderAvatar = () => {
    if (!userData) return null;
    
    return userData.avatar ? (
      <img
        key={userData.avatar} // Force re-render when URL changes
        src={userData.avatar}
        alt={`${userData.name}'s profile`}
        className={styles.avatar}
        onError={(e) => {
          console.error('Error loading avatar image:', e);
          e.target.style.display = 'none';
          e.target.nextSibling.style.display = 'flex';
        }}
        onLoad={() => {
          console.log('Avatar image loaded successfully:', userData.avatar);
        }}
      />
    ) : (
      <div className={styles.avatarFallback}>
        {getInitials(userData.name)}
      </div>
    );
  };

  // Add a useEffect to monitor userData changes
  useEffect(() => {
    console.log('userData updated:', userData);
  }, [userData]);

  const handlePreferencesSubmit = async (e) => {
    e.preventDefault();
    try {
      console.log('Submitting preferences:', preferences);
      const token = await auth.currentUser?.getIdToken();
      const response = await api.put(
        endpoints.user.update,
        { email_preferences: preferences },
        { headers: { Authorization: `Bearer ${token}` }}
      );

      if (response.data) {
        console.log('Preferences updated successfully:', response.data);
        await fetchUserData();
        setShowPreferencesModal(false);
        toast.success('Communication preferences updated successfully');
      }
    } catch (err) {
      console.error('Error updating preferences:', err);
      toast.error(err.response?.data?.message || 'Failed to update preferences');
    }
  };

  const handlePreferenceChange = (preference) => {
    console.log('Updating preference:', preference);
    setPreferences(prev => ({
      ...prev,
      [preference]: !prev[preference]
    }));
  };

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase() || 'U';
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className={styles.accountPage}>
      <div className={styles.accountHeader}>
        <h1>Account Settings</h1>
        <button 
          className={styles.buttonSecondary}
          onClick={() => auth.signOut()}
        >
          Log Out
        </button>
      </div>

      <OnboardingChecklist userData={userData} />
      <CollectorOnboardingChecklist />

      <RoleManagement />

      {userData && (
        <>
          <div className={styles.accountDetails}>
            <div className={styles.avatarSection}>
              <div 
                {...getRootProps()} 
                className={`${styles.avatarWrapper} ${isUploading ? styles.loading : ''}`}
              >
                <input {...getInputProps()} />
                {renderAvatar()}
                <div className={styles.avatarOverlay}>
                  {isUploading ? (
                    <>
                      <div className={styles.loadingSpinner} />
                      <span>{uploadProgress}%</span>
                    </>
                  ) : (
                    <>
                      <span>Change Photo</span>
                      <p className={styles.uploadRequirements}>
                        JPEG, JPG, or PNG • Max 5MB
                      </p>
                    </>
                  )}
                </div>
              </div>
              <div className={styles.userInfo}>
                <h2>{userData.name}</h2>
                <p>{userData.email}</p>
              </div>
            </div>

            <div className={styles.detailsHeader}>Account Information</div>
            <div className={styles.detailsContent}>
              <div className={styles.detailsGrid}>
                <div className={styles.detailItem}>
                  <div className={styles.detailLabel}>Name</div>
                  <div className={styles.detailRow}>
                    <div className={styles.detailValue}>{userData.name}</div>
                    <button 
                      className={styles.editButton}
                      onClick={() => setShowEditModal(true)}
                    >
                      Edit
                    </button>
                  </div>
                </div>
                <div className={styles.detailItem}>
                  <div className={styles.detailLabel}>Email</div>
                  <div className={styles.detailRow}>
                    <div className={styles.detailValue}>{userData.email}</div>
                    <button 
                      className={styles.editButton}
                      onClick={() => setShowEmailModal(true)}
                    >
                      Edit
                    </button>
                  </div>
                </div>
              </div>
              <div className={styles.accountActions}>
                <button 
                  className={styles.buttonSecondary}
                  onClick={() => setShowPasswordModal(true)}
                >
                  Change Password
                </button>
                <button 
                  className={styles.buttonSecondary}
                  onClick={() => setShowPreferencesModal(true)}
                >
                  Communication Preferences
                </button>
              </div>
            </div>
          </div>

          {showEditModal && (
            <div className={`${styles.modal} ae-dialog-scroll`}>
              <div className={styles.modalContent}>
                <h3>Edit Name</h3>
                <form onSubmit={handleEditSubmit}>
                  <div className={styles.formGroup}>
                    <label htmlFor="name">Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={editForm.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className={styles.modalActions}>
                    <button 
                      type="button" 
                      className={styles.secondaryButton}
                      onClick={() => setShowEditModal(false)}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className={styles.primaryButton}
                    >
                      Save Changes
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {showEmailModal && (
            <div className={`${styles.modal} ae-dialog-scroll`}>
              <div className={styles.modalContent}>
                <h3>Change Email Address</h3>
                <form onSubmit={handleEmailChange}>
                  <div className={styles.formGroup}>
                    <label htmlFor="currentPassword">Current Password</label>
                    <input
                      type="password"
                      id="currentPassword"
                      name="currentPassword"
                      value={emailForm.currentPassword}
                      onChange={(e) => setEmailForm(prev => ({
                        ...prev,
                        currentPassword: e.target.value
                      }))}
                      required
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label htmlFor="newEmail">New Email</label>
                    <input
                      type="email"
                      id="newEmail"
                      name="newEmail"
                      value={emailForm.newEmail}
                      onChange={(e) => setEmailForm(prev => ({
                        ...prev,
                        newEmail: e.target.value
                      }))}
                      required
                    />
                  </div>
                  <div className={styles.modalActions}>
                    <button 
                      type="button" 
                      className={styles.secondaryButton}
                      onClick={() => setShowEmailModal(false)}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className={styles.primaryButton}
                    >
                      Update Email
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {showPasswordModal && (
            <div className={`${styles.modal} ae-dialog-scroll`}>
              <div className={styles.modalContent}>
                <h3>Change Password</h3>
                <form onSubmit={handlePasswordChange}>
                  <div className={styles.formGroup}>
                    <label htmlFor="currentPasswordForPw">Current Password</label>
                    <input
                      type="password"
                      id="currentPasswordForPw"
                      name="currentPassword"
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm(prev => ({
                        ...prev,
                        currentPassword: e.target.value
                      }))}
                      required
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label htmlFor="newPassword">New Password</label>
                    <input
                      type="password"
                      id="newPassword"
                      name="newPassword"
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm(prev => ({
                        ...prev,
                        newPassword: e.target.value
                      }))}
                      required
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label htmlFor="confirmPassword">Confirm New Password</label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm(prev => ({
                        ...prev,
                        confirmPassword: e.target.value
                      }))}
                      required
                    />
                  </div>
                  <div className={styles.modalActions}>
                    <button 
                      type="button" 
                      className={styles.secondaryButton}
                      onClick={() => setShowPasswordModal(false)}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className={styles.primaryButton}
                    >
                      Update Password
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {showPreferencesModal && (
            <div className={`${styles.modal} ae-dialog-scroll`}>
              <div className={styles.modalContent}>
                <h3>Communication Preferences</h3>
                <form onSubmit={handlePreferencesSubmit}>
                  <div className={styles.preferencesGrid}>
                    <label className={styles.checkbox}>
                      <input
                        type="checkbox"
                        checked={preferences.newsletters}
                        onChange={() => handlePreferenceChange('newsletters')}
                      />
                      <span>Newsletters</span>
                    </label>
                    <label className={styles.checkbox}>
                      <input
                        type="checkbox"
                        checked={preferences.artworkUpdates}
                        onChange={() => handlePreferenceChange('artworkUpdates')}
                      />
                      <span>Artwork Updates</span>
                    </label>
                    <label className={styles.checkbox}>
                      <input
                        type="checkbox"
                        checked={preferences.exhibitionNotices}
                        onChange={() => handlePreferenceChange('exhibitionNotices')}
                      />
                      <span>Exhibition Notices</span>
                    </label>
                    <label className={styles.checkbox}>
                      <input
                        type="checkbox"
                        checked={preferences.marketingEmails}
                        onChange={() => handlePreferenceChange('marketingEmails')}
                      />
                      <span>Marketing Emails</span>
                    </label>
                  </div>
                  <div className={styles.modalActions}>
                    <button 
                      type="button" 
                      className={styles.secondaryButton}
                      onClick={() => setShowPreferencesModal(false)}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className={styles.primaryButton}
                    >
                      Save Preferences
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <PaymentSettings user={userData} />

          {userData.role === 'collector' && (
            <div className={styles.roleSwitchSection}>
              <h3>Switch Account Type</h3>
              <p>You can switch your account type to access artist features.</p>
              <button 
                className={styles.primaryButton}
                onClick={() => handleRoleUpdate('artist')}
                disabled={roleUpdateLoading}
              >
                {roleUpdateLoading ? 'Updating...' : 'Switch to Artist Account'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Account; 
